import React, { useState, useEffect } from 'react';
import { Check, BookOpen, Edit3, Globe, TrendingUp, Star, Clock, Shield, Award, ChevronLeft, ChevronRight, Sparkles, Send, MessageCircle, Phone, Mail } from 'lucide-react';
import BlobButton from '../components/BlobButton';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { submitLeadWithGeo } from '../services/apiService';
import { useGeolocation } from '../hooks/useGeolocation';
import MobileSlider from '../components/MobileSlider';
import ContactFormModal from '../components/ContactFormModal';

const LPAudiobook = () => {
  const { country, isLoading: geoLoading, error: geoError, retry: retryGeo } = useGeolocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [ctaFormData, setCtaFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectDetails: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const testimonialsPerPage = 4;

  useEffect(() => {
    document.body.style.paddingTop = '0';
    return () => {
      document.body.style.paddingTop = '';
    };
  }, []);

  // Countdown timer using same logic as home page
  const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
  const ONE_HOUR = 60 * 60 * 1000;

  const getInitialEndTime = () => {
    const stored = localStorage.getItem('discountEndTime');
    if (stored) {
      return parseInt(stored);
    }
    const endTime = Date.now() + SEVEN_DAYS;
    localStorage.setItem('discountEndTime', endTime.toString());
    return endTime;
  };

  const [endTime, setEndTime] = useState(getInitialEndTime());
  const [timeLeft, setTimeLeft] = useState(endTime - Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = endTime - Date.now();
      
      if (remaining <= ONE_HOUR && remaining > 0) {
        const newEndTime = Date.now() + SEVEN_DAYS;
        setEndTime(newEndTime);
        localStorage.setItem('discountEndTime', newEndTime.toString());
        setTimeLeft(SEVEN_DAYS);
      } else {
        setTimeLeft(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endTime]);

  const formatTime = (ms) => {
    if (ms <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((ms % (60 * 1000)) / 1000);
    
    return { days, hours, minutes, seconds };
  };

  const time = formatTime(timeLeft);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if geolocation is still loading
    if (geoLoading) {
      setSubmitMessage('Please wait while we detect your location...');
      return;
    }
    
    // Check if geolocation failed - MANDATORY
    if (geoError) {
      setSubmitMessage('Unable to detect your location. Please check your internet connection and try again.');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/lp-leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          manuscriptStatus: 'from-hero-form-audiobook'
        }),
      });

      if (response.ok) {
        setSubmitMessage('Thank you! Our audiobook expert will contact you within 24 hours.');
        setFormData({ fullName: '', email: '', phone: '', message: '' });
        
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-XXXXXXXXX/XXXXXXXXXXXXX'
          });
        }
        
        if (window.fbq) {
          window.fbq('track', 'Lead');
        }
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCtaFormSubmit = async (e) => {
    e.preventDefault();
    
    // Check if geolocation is available - MANDATORY
    if (geoLoading) {
      alert('Please wait while we detect your location...');
      return;
    }
    
    if (geoError) {
      alert('Unable to detect your location. Please try again.');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/lp-leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: ctaFormData.name,
          email: ctaFormData.email,
          phone: ctaFormData.phone,
          manuscriptStatus: 'from-cta-form-audiobook'
        }),
      });

      if (response.ok) {
        alert('Thank you! Your message has been sent successfully.');
        setCtaFormData({ name: '', email: '', phone: '', projectDetails: '' });
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (error) {
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCtaFormChange = (e) => {
    setCtaFormData({
      ...ctaFormData,
      [e.target.name]: e.target.value
    });
  };

  const portfolioItems = [
    {
      id: 1,
      image: 'https://customer-assets.emergentagent.com/job_77845016-0cff-4a07-b056-0e435a63b242/artifacts/hr4j2zra_Fiction%20Book%20%281%29.png',
      title: 'The Picture of Dorian Gray',
      category: 'fiction',
      author: 'Oscar Wilde',
      genre: 'Fiction & Literature'
    },
    {
      id: 2,
      image: 'https://customer-assets.emergentagent.com/job_77845016-0cff-4a07-b056-0e435a63b242/artifacts/g7ef28u2_Educational%20Book.png',
      title: 'The Quantum Screen',
      category: 'educational',
      author: 'Samuel Avery',
      genre: 'Educational & Science'
    },
    {
      id: 3,
      image: 'https://customer-assets.emergentagent.com/job_77845016-0cff-4a07-b056-0e435a63b242/artifacts/hw7vz9yq_Fiction%20Book%20%282%29.png',
      title: 'The After House',
      category: 'fiction',
      author: 'Michael Phillip Cash',
      genre: 'Fiction & Mystery'
    },
    {
      id: 4,
      image: 'https://customer-assets.emergentagent.com/job_77845016-0cff-4a07-b056-0e435a63b242/artifacts/zwxalo6i_romance%20%281%29.png',
      title: 'American Queen',
      category: 'romance',
      author: 'Sierra Simone',
      genre: 'Romance'
    },
    {
      id: 5,
      image: 'https://customer-assets.emergentagent.com/job_77845016-0cff-4a07-b056-0e435a63b242/artifacts/h46wwenm_Biography%20Book.png',
      title: 'Blood & Honey',
      category: 'biography',
      author: 'Shelby Mahurin',
      genre: 'Biography & Memoir'
    },
    {
      id: 6,
      image: 'https://customer-assets.emergentagent.com/job_77845016-0cff-4a07-b056-0e435a63b242/artifacts/10yt2mrh_romance%20%282%29.png',
      title: 'Break the Girl',
      category: 'romance',
      author: 'Rachel Jonas & Nikki Thorne',
      genre: 'Romance'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Books' },
    { id: 'fiction', name: 'Fiction' },
    { id: 'romance', name: 'Romance' },
    { id: 'biography', name: 'Biography' },
    { id: 'educational', name: 'Educational' }
  ];

  const filteredItems = activeCategory === 'all'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === activeCategory);

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Bestselling Author',
      image: 'https://images.unsplash.com/photo-1722371131066-e37037ecf109?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
      text: 'Working with this publishing company was a game-changer for my writing career. Their team handled everything from editing to marketing with exceptional professionalism.',
      rating: 5,
      bookTitle: 'The Journey Within'
    },
    {
      name: 'Michael Chen',
      role: 'Business Author',
      image: 'https://images.unsplash.com/photo-1732166946438-90d000cd032a?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
      text: 'The level of expertise and attention to detail exceeded all my expectations. From manuscript to bestseller, they guided me every step of the way.',
      rating: 5,
      bookTitle: 'Scaling Success'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Fiction Writer',
      image: 'https://images.unsplash.com/photo-1722371131066-e37037ecf109?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
      text: 'I could not have asked for a better publishing partner. Their creative team brought my vision to life with stunning cover designs and professional formatting.',
      rating: 5,
      bookTitle: 'Whispers in Time'
    },
    {
      name: 'James Anderson',
      role: 'Non-Fiction Author',
      image: 'https://images.unsplash.com/photo-1732166946438-90d000cd032a?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
      text: 'Outstanding service from start to finish. The marketing support helped my book reach audiences I never thought possible. Highly recommended!',
      rating: 5,
      bookTitle: 'Leadership Principles'
    },
    {
      name: 'Jennifer Williams',
      role: 'Romance Author',
      image: 'https://images.unsplash.com/photo-1722371131066-e37037ecf109?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
      text: 'The editorial team understood my voice perfectly and helped me craft a story that resonated with readers. My book hit the bestseller list within weeks!',
      rating: 5,
      bookTitle: 'Hearts Entwined'
    },
    {
      name: 'David Thompson',
      role: 'Self-Help Author',
      image: 'https://images.unsplash.com/photo-1732166946438-90d000cd032a?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
      text: 'Professional, responsive, and incredibly knowledgeable. They turned my manuscript into a polished book that has changed thousands of lives.',
      rating: 5,
      bookTitle: 'Mindful Living'
    },
    {
      name: 'Amanda Foster',
      role: 'Young Adult Author',
      image: 'https://images.unsplash.com/photo-1722371131066-e37037ecf109?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
      text: 'They believed in my story when others did not. The cover design they created perfectly captures the essence of my book. Forever grateful!',
      rating: 5,
      bookTitle: 'Starlight Academy'
    },
    {
      name: 'Robert Martinez',
      role: 'Historical Fiction Author',
      image: 'https://images.unsplash.com/photo-1732166946438-90d000cd032a?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
      text: 'Their research team helped me ensure historical accuracy while maintaining an engaging narrative. The result was a critically acclaimed novel.',
      rating: 5,
      bookTitle: 'Echoes of the Past'
    }
  ];

  const packages = [
    {
      name: 'Basic Package',
      originalPrice: '$999',
      discountedPrice: '$599',
      savings: 'Save $400',
      features: [
        'Professional formatting',
        'Custom cover design',
        'ISBN registration',
        'Amazon KDP publishing',
        'Author copies (5 books)',
        'Basic marketing guide'
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      name: 'Premium Package',
      originalPrice: '$1,999',
      discountedPrice: '$1,199',
      savings: 'Save $800',
      features: [
        'Professional editing (up to 80k words)',
        'Premium cover design',
        'ISBN & barcode',
        'Multi-platform publishing (Amazon, B&N, more)',
        'Author website setup',
        'Social media marketing kit',
        '20 author copies',
        '3-month marketing support'
      ],
      cta: 'Publish My Book',
      popular: true
    },
    {
      name: 'Elite Package',
      originalPrice: '$3,299',
      discountedPrice: '$1,999',
      savings: 'Save $1,300',
      features: [
        'Full manuscript editing & proofreading',
        'Elite cover design + interior illustrations',
        'ISBN, barcode & copyright registration',
        'Worldwide distribution',
        'Professional author branding',
        'PR press release',
        'Book trailer video',
        '50 author copies',
        '6-month marketing & promotion'
      ],
      cta: 'Claim Elite Offer',
      popular: false
    }
  ];

  const totalPages = Math.ceil(testimonials.length / testimonialsPerPage);
  const currentTestimonials = testimonials.slice(
    currentPage * testimonialsPerPage,
    (currentPage + 1) * testimonialsPerPage
  );

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  return (
    <div className="min-h-screen bg-white relative">
      <script async src="https://www.googletagmanager.com/gtag/js?id=AW-XXXXXXXXX"></script>
        
        {/* Navigation Button Styles */}
        <style>{`
          .lp-nav-btn {
            position: relative;
            padding: 10px 20px;
            text-align: center;
            text-transform: uppercase;
            font-size: 12px;
            font-weight: bold;
            outline: none;
            border: none;
            cursor: pointer;
            border-radius: 25px;
            transition: all 0.3s ease;
            z-index: 1;
          }

          .lp-nav-btn--outline {
            color: #B45309;
            background-color: transparent;
            border: 2px solid #B45309;
          }

          .lp-nav-btn--outline:hover {
            color: #FFFFFF;
            border-color: #B45309;
          }

          .lp-nav-btn--filled {
            color: #FFFFFF;
            background-color: #B45309;
            border: 2px solid #B45309;
          }

          .lp-nav-btn--filled:hover {
            color: #B45309;
            background-color: transparent;
          }

          .lp-nav-btn__inner {
            z-index: -1;
            overflow: hidden;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            border-radius: 25px;
          }

          .lp-nav-btn__blobs {
            position: relative;
            display: block;
            height: 100%;
            filter: url('#goo');
          }

          .lp-nav-btn__blob {
            position: absolute;
            top: 2px;
            width: 25%;
            height: 100%;
            background: #B45309;
            border-radius: 100%;
            transform: translate3d(0, 150%, 0) scale(1.7);
            transition: transform 0.45s;
          }

          .lp-nav-btn__blob:nth-child(1) { left: 0%; transition-delay: 0s; }
          .lp-nav-btn__blob:nth-child(2) { left: 30%; transition-delay: 0.08s; }
          .lp-nav-btn__blob:nth-child(3) { left: 60%; transition-delay: 0.16s; }
          .lp-nav-btn__blob:nth-child(4) { left: 90%; transition-delay: 0.24s; }

          .lp-nav-btn--outline:hover .lp-nav-btn__blob {
            transform: translateZ(0) scale(1.7);
          }

          .lp-nav-btn--filled:hover .lp-nav-btn__blob {
            transform: translateZ(0) scale(1.7);
            background: #FFFFFF;
          }
        `}</style>
      
      {/* Animated Background - Covers entire page body */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <g id="bookIcon">
              <rect x="-8" y="-10" width="16" height="20" stroke="#b45309" strokeWidth="1.5" fill="none" rx="1"/>
              <line x1="-8" y1="-3" x2="8" y2="-3" stroke="#b45309" strokeWidth="1"/>
              <line x1="-8" y1="2" x2="8" y2="2" stroke="#b45309" strokeWidth="1"/>
              <line x1="0" y1="-10" x2="0" y2="10" stroke="#b45309" strokeWidth="1"/>
            </g>
            
            <g id="penIcon">
              <path d="M 0 -12 L 0 10" stroke="#b45309" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
              <path d="M -2 -12 L 2 -12" stroke="#b45309" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M 0 10 L -2 12 L 0 11 L 2 12 Z" stroke="#b45309" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
            </g>
            
            <g id="inkpotIcon">
              <circle cx="0" cy="0" r="6" stroke="#b45309" strokeWidth="1.5" fill="none"/>
              <rect x="-3" y="-8" width="6" height="8" stroke="#b45309" strokeWidth="1.5" fill="none"/>
              <line x1="-4" y1="-2" x2="4" y2="-2" stroke="#b45309" strokeWidth="1"/>
            </g>
            
            <g id="quillIcon">
              <path d="M 0 -12 Q -3 -6, -2 0" stroke="#b45309" strokeWidth="1.5" fill="none"/>
              <path d="M 0 -12 Q 3 -6, 2 0" stroke="#b45309" strokeWidth="1.5" fill="none"/>
              <line x1="0" y1="-10" x2="-4" y2="-4" stroke="#b45309" strokeWidth="0.8"/>
              <line x1="0" y1="-8" x2="-3" y2="-3" stroke="#b45309" strokeWidth="0.8"/>
            </g>
          </defs>

          <g className="animate-line-1">
            <path 
              id="path1"
              stroke="#b45309"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="8 12"
              d="M -200 150 Q 200 100, 600 200 T 1400 150 T 2200 200"
              fill="transparent"
            />
            <use href="#bookIcon" className="icon-motion">
              <animateMotion dur="28s" repeatCount="indefinite">
                <mpath href="#path1"/>
              </animateMotion>
            </use>
          </g>

          <g className="animate-line-2">
            <path 
              id="path2"
              stroke="#b45309"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="8 12"
              d="M 1900 -100 Q 1600 100, 1300 300 Q 1000 500, 700 600 Q 400 700, 100 800"
              fill="transparent"
            />
            <use href="#quillIcon" className="icon-motion">
              <animateMotion dur="32s" repeatCount="indefinite">
                <mpath href="#path2"/>
              </animateMotion>
            </use>
          </g>

          <g className="animate-line-3">
            <path 
              id="path3"
              stroke="#b45309"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="8 12"
              d="M -100 400 C 200 300, 400 500, 700 400 S 1200 300, 1500 400 S 1800 500, 2000 400"
              fill="transparent"
            />
            <use href="#bookIcon" className="icon-motion">
              <animateMotion dur="30s" repeatCount="indefinite">
                <mpath href="#path3"/>
              </animateMotion>
            </use>
          </g>
        </svg>

        <style jsx>{`
          @keyframes lineFlow1 {
            0% { opacity: 0; transform: translateX(-100px) translateY(0); }
            15% { opacity: 0.15; }
            85% { opacity: 0.15; }
            100% { opacity: 0; transform: translateX(400px) translateY(-50px); }
          }

          @keyframes lineFlow2 {
            0% { opacity: 0; transform: translateX(100px) translateY(-100px); }
            20% { opacity: 0.15; }
            80% { opacity: 0.15; }
            100% { opacity: 0; transform: translateX(-250px) translateY(200px); }
          }

          @keyframes lineFlow3 {
            0% { opacity: 0; transform: translateX(-80px) translateY(50px); }
            18% { opacity: 0.15; }
            82% { opacity: 0.15; }
            100% { opacity: 0; transform: translateX(300px) translateY(120px); }
          }

          @keyframes iconFade {
            0%, 100% { opacity: 0; }
            10%, 90% { opacity: 0.15; }
          }

          .animate-line-1 { animation: lineFlow1 28s ease-in-out infinite; animation-delay: 0s; }
          .animate-line-2 { animation: lineFlow2 32s ease-in-out infinite; animation-delay: 6s; }
          .animate-line-3 { animation: lineFlow3 30s ease-in-out infinite; animation-delay: 12s; }
          .icon-motion { animation: iconFade 28s ease-in-out infinite; }
        `}</style>
      </div>

      {/* Landing Page Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white shadow-sm" style={{ zIndex: 100 }}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_book-service-lp/artifacts/f7adg5p2_logo-dark.png" 
                alt="Rushmore Publishing" 
                className="h-12 w-auto"
              />
            </div>

            {/* Right Side - Email, Call & Chat */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Email Button */}
              <a 
                href="mailto:info@rushmorepublishing.com" 
                className="flex items-center gap-2 px-3 md:px-4 py-2 text-amber-700 hover:text-amber-800 font-medium transition-colors whitespace-nowrap"
                title="Email us"
              >
                <Mail className="w-5 h-5" />
                <span className="hidden lg:inline text-sm">info@rushmorepublishing.com</span>
              </a>

                            {/* Call Now Button - Outlined style with blob animation */}
              <a 
                href="tel:+18669788498" 
                className="lp-nav-btn lp-nav-btn--outline flex items-center gap-2 whitespace-nowrap"
                title="Call us now"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden md:inline text-xs font-bold">Call Now</span>
                <span className="lp-nav-btn__inner">
                  <span className="lp-nav-btn__blobs">
                    <span className="lp-nav-btn__blob"></span>
                    <span className="lp-nav-btn__blob"></span>
                    <span className="lp-nav-btn__blob"></span>
                    <span className="lp-nav-btn__blob"></span>
                  </span>
                </span>
              </a>

                            {/* Chat Now Button - Filled style with blob animation */}
              <button 
                className="js-chat open-chat lp-nav-btn lp-nav-btn--filled flex items-center gap-2 whitespace-nowrap"
                title="Chat with us"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="hidden md:inline text-xs font-bold">Chat Now</span>
                <span className="lp-nav-btn__inner">
                  <span className="lp-nav-btn__blobs">
                    <span className="lp-nav-btn__blob"></span>
                    <span className="lp-nav-btn__blob"></span>
                    <span className="lp-nav-btn__blob"></span>
                    <span className="lp-nav-btn__blob"></span>
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-8 lg:py-10 px-4 overflow-hidden lg:min-h-screen lg:flex lg:items-center" style={{ paddingTop: '100px', zIndex: 1 }}>
        {/* Background Image */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1920&h=1080&fit=crop)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: -1
          }}
        >
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-50/95 via-white/90 to-amber-50/95"></div>
        </div>

        <div className="container mx-auto relative" style={{ zIndex: 10 }}>
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left Column - Content */}
            <div className="flex flex-col justify-center space-y-4 lg:space-y-5 lg:pr-12 h-full">
              <div className="inline-flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-full self-start">
                <Award className="w-5 h-5 text-amber-700" />
                <span className="text-amber-800 font-semibold text-sm">500+ Audiobooks Produced</span>
              </div>
              
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight">
                Transform Your Book Into a <span className="text-amber-600">Professional Audiobook!</span>
              </h1>
              
              <p className="text-xl text-gray-700 leading-relaxed">
                From professional narration to distribution on Audible, iTunes, and Google Play - we handle it all.
              </p>

              {/* USP Points */}
              <div className="space-y-2 lg:space-y-3 pt-2">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-100 p-2 rounded-full">
                    <Check className="w-5 h-5 text-amber-700" />
                  </div>
                  <span className="text-gray-700 font-medium">Professional voice narration</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-amber-100 p-2 rounded-full">
                    <Check className="w-5 h-5 text-amber-700" />
                  </div>
                  <span className="text-gray-700 font-medium">Distribution to all major platforms</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-amber-100 p-2 rounded-full">
                    <Check className="w-5 h-5 text-amber-700" />
                  </div>
                  <span className="text-gray-700 font-medium">Studio-quality production</span>
                </div>
              </div>
            </div>

            {/* Right Column - Form (Aligned to right) */}
            <div className="flex justify-end">
              <div className="w-full max-w-md rounded-2xl p-5 lg:p-6 shadow-2xl bg-white" style={{ backgroundColor: '#ffffff', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
              {/* Discount Badge */}
              <div className="flex items-center justify-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-amber-700" />
                <span className="text-amber-700 font-bold text-lg">40% OFF First-Time Orders!</span>
              </div>
              
              {/* Form Heading */}
              <div className="text-center mb-5 lg:mb-6">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                  Bring Your Book To Life<br />
                  <span className="text-amber-700">With Audio</span>
                </h2>
              </div>

              {/* Form - Underlined inputs like Home Page */}
              <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-5">
                <div className="relative">
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Your Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-2 text-gray-900 placeholder:text-gray-500 transition-colors"
                  />
                </div>

                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-2 text-gray-900 placeholder:text-gray-500 transition-colors"
                  />
                </div>

                {/* Country Field - Auto-detected */}
                <div className="relative">
                  <input
                    type="text"
                    name="country"
                    placeholder={geoLoading ? "Detecting country..." : "Country"}
                    value={country}
                    disabled
                    className="w-full bg-gray-50 border-0 border-b-2 border-gray-300 outline-none px-0 py-2 text-gray-700 placeholder:text-gray-500 cursor-not-allowed"
                  />
                </div>

                {/* Phone Number with Country Code */}
                <div className="relative flex gap-2 items-end">
                  <select className="bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none py-2 text-gray-900 transition-colors">
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+81">🇯🇵 +81</option>
                    <option value="+86">🇨🇳 +86</option>
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="flex-1 bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-2 text-gray-900 placeholder:text-gray-500 transition-colors"
                  />
                </div>

                <div className="relative">
                  <textarea
                    name="message"
                    placeholder="Tell us about your book"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="2"
                    className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-2 text-gray-900 placeholder:text-gray-500 transition-colors resize-none"
                  />
                </div>

                {/* Countdown Timer - Smaller, Above Button, Horizontal */}
                <div className="flex items-center justify-center gap-2 py-2">
                  <Clock className="w-4 h-4 text-amber-700" />
                  <span className="text-xs font-semibold text-amber-700">Offer ends in:</span>
                  <div className="flex gap-2">
                    <div className="text-center">
                      <div className="bg-amber-700 text-white rounded px-2 py-1">
                        <span className="text-sm font-bold">{time.days}</span>
                      </div>
                      <span className="text-[10px] text-gray-600">D</span>
                    </div>
                    <div className="text-center">
                      <div className="bg-amber-700 text-white rounded px-2 py-1">
                        <span className="text-sm font-bold">{time.hours}</span>
                      </div>
                      <span className="text-[10px] text-gray-600">H</span>
                    </div>
                    <div className="text-center">
                      <div className="bg-amber-700 text-white rounded px-2 py-1">
                        <span className="text-sm font-bold">{time.minutes}</span>
                      </div>
                      <span className="text-[10px] text-gray-600">M</span>
                    </div>
                    <div className="text-center">
                      <div className="bg-amber-700 text-white rounded px-2 py-1">
                        <span className="text-sm font-bold">{time.seconds}</span>
                      </div>
                      <span className="text-[10px] text-gray-600">S</span>
                    </div>
                  </div>
                </div>

                <BlobButton
                  type="submit"
                  variant="default"
                  className="w-full whitespace-nowrap"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Claim 40% OFF & Start Publishing'}
                </BlobButton>

                {submitMessage && (
                  <div className={`p-3 rounded-lg text-center text-sm ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {submitMessage}
                  </div>
                )}
              </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Book Publishing Section */}
      <section className="py-20 bg-white px-4 relative" style={{ zIndex: 1 }}>
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1512820790803-83ca734da794?w=800&h=600&fit=crop"
                alt="Book Publishing"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                What is <span className="text-amber-600">Audiobook Production?</span>
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Audiobook production is the complete process of transforming your written book into a professionally narrated audio experience. It encompasses narrator selection, studio recording, audio editing, mastering, and distribution to platforms like Audible, iTunes, and Google Play.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Whether you're a debut author or bestselling writer, professional audiobook production expands your reach to the rapidly growing audience of audio listeners, creating a new revenue stream while making your content accessible to readers who prefer listening.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-amber-100 p-2 rounded-full mt-1">
                    <Check className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Professional Editing & Proofreading</h4>
                    <p className="text-gray-600">Ensure your manuscript is error-free and polished</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-amber-100 p-2 rounded-full mt-1">
                    <Check className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Cover Design & Formatting</h4>
                    <p className="text-gray-600">Create eye-catching designs that sell</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-amber-100 p-2 rounded-full mt-1">
                    <Check className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Global Distribution</h4>
                    <p className="text-gray-600">Get your book on major platforms worldwide</p>
                  </div>
                </div>
              </div>
                          
              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 mt-8">
                <BlobButton
                  onClick={() => setIsModalOpen(true)}
                  variant="default"
                  className="whitespace-nowrap"
                >
                  Start Your Audiobook Journey
                </BlobButton>
                <BlobButton
                  onClick={() => {
                    if (window.zE) {
                      window.zE('webWidget', 'open');
                    }
                  }}
                  variant="default"
                  className="open-chat whitespace-nowrap"
                >
                  Discuss Your Book
                </BlobButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-amber-50 px-4 relative" style={{ zIndex: 1 }}>
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Your <span className="text-amber-600">Audiobook Production?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine professional narrators, studio-quality production, and seamless distribution to bring your book to audio listeners worldwide
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-amber-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Complete Publishing Solutions</h3>
              <p className="text-gray-600">
                We handle every aspect of publishing - from manuscript preparation to marketing and distribution. One-stop solution for all your needs.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-amber-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Industry Expertise</h3>
              <p className="text-gray-600">
                Our team includes published authors, experienced editors, and marketing professionals who understand what it takes to succeed.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-amber-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Global Reach</h3>
              <p className="text-gray-600">
                We publish books on Amazon, Barnes & Noble, and 30+ countries worldwide, giving your work maximum exposure.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-amber-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Author-First Approach</h3>
              <p className="text-gray-600">
                You retain 100% ownership and all rights. We work for you, not the other way around. Your success is our priority.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-amber-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Marketing Support</h3>
              <p className="text-gray-600">
                Get professional marketing strategies, social media support, and promotional tools to boost your book sales.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Edit3 className="w-8 h-8 text-amber-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Quality Assurance</h3>
              <p className="text-gray-600">
                Every book goes through rigorous quality checks to ensure it meets professional standards and reader expectations.
              </p>
            </div>
          </div>
                  
          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 justify-center mt-12">
            <BlobButton
              onClick={() => setIsModalOpen(true)}
              variant="default"
              className="whitespace-nowrap"
            >
              Start Your Audiobook Journey
            </BlobButton>
            <BlobButton
              onClick={() => {
                if (window.zE) {
                  window.zE('webWidget', 'open');
                }
              }}
              variant="default"
              className="open-chat whitespace-nowrap"
            >
              Discuss Your Book
            </BlobButton>
          </div>
        </div>
      </section>

      {/* Publishing Packages */}
      <section className="py-20 bg-white px-4 relative" style={{ zIndex: 1 }}>
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Choose a Publishing Package That Fits Your Goals
            </h2>
            <p className="text-xl text-gray-600">
              All packages include 40% discount - limited time only!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 shadow-lg relative flex flex-col ${
                  pkg.popular ? 'ring-4 ring-amber-500 transform scale-105' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-amber-600 text-white px-6 py-1 rounded-full text-sm font-bold">
                    MOST POPULAR
                  </div>
                )}

                <h3 className="text-2xl font-bold text-gray-900 mb-6">{pkg.name}</h3>
                
                

                <ul className="space-y-3 mb-8 flex-grow">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <BlobButton 
                  onClick={() => setIsModalOpen(true)}
                  variant={pkg.popular ? 'white' : 'default'}
                  className="w-full mt-auto"
                >
                  Get a Quote
                </BlobButton>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-gray-50 px-4 relative" style={{ zIndex: 1 }}>
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">
              Get published in 3 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="bg-amber-600 text-white w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                01
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Narrator Selection</h3>
              <p className="text-gray-600 leading-relaxed">
                Choose from our roster of professional narrators, or we help you find the perfect voice that matches your book's tone.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-amber-600 text-white w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                02
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Recording Editing & Formatting Production</h3>
              <p className="text-gray-600 leading-relaxed">
                Professional studio recording with expert audio engineering, editing, and mastering for superior sound quality.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-amber-600 text-white w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                03
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Distribution Publishing & Distribution Promotion</h3>
              <p className="text-gray-600 leading-relaxed">
                We distribute your audiobook to Audible, iTunes, Google Play, and more, with promotional support to maximize reach.
              </p>
            </div>
          </div>
                  
          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 justify-center mt-12">
            <BlobButton
              onClick={() => setIsModalOpen(true)}
              variant="default"
              className="whitespace-nowrap"
            >
              Start Your Audiobook Journey
            </BlobButton>
            <BlobButton
              onClick={() => {
                if (window.zE) {
                  window.zE('webWidget', 'open');
                }
              }}
              variant="default"
              className="open-chat whitespace-nowrap"
            >
              Discuss Your Book
            </BlobButton>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="py-20 bg-white px-4 relative" style={{ zIndex: 1 }}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-700 rounded-full mb-6">
              <span className="text-amber-700 text-sm font-semibold tracking-wider">OUR PORTFOLIO</span>
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              Featured <span className="text-amber-700">Published Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover some of the remarkable books that the authors have published and promoted.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map(category => (
              <Button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                variant={activeCategory === category.id ? 'default' : 'outline'}
                className={`px-8 py-6 transition-all rounded-full shadow-lg hover:shadow-xl ${
                  activeCategory === category.id
                    ? 'bg-amber-700 hover:bg-amber-800 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {category.name}
              </Button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item, index) => (
              <Card
                key={item.id}
                className="group overflow-hidden shadow-2xl hover:shadow-xl transition-all duration-500 rounded-2xl"
              >
                <div className="relative h-96 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-sm text-gray-300 mb-1">by {item.author}</p>
                      <p className="text-sm text-amber-400">{item.genre}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-amber-50 px-4 relative" style={{ zIndex: 1 }}>
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-700 rounded-full mb-6">
              <span className="text-amber-700 text-sm font-semibold tracking-wider">OUR ACHIEVEMENTS</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Publishing Excellence by the <span className="text-amber-700">Numbers</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-20 h-20 mx-auto mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_77845016-0cff-4a07-b056-0e435a63b242/artifacts/s71u7dpq_Books%20Published.gif" 
                  alt="Books Published"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-5xl font-bold text-gray-900 mb-2">500+</div>
              <div className="text-xl font-semibold text-amber-700 mb-2">Books Published</div>
              <div className="text-gray-600">Across all genres and formats</div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-20 h-20 mx-auto mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_77845016-0cff-4a07-b056-0e435a63b242/artifacts/56iauid6_Awards%20Won.gif" 
                  alt="Awards Won"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-5xl font-bold text-gray-900 mb-2">50+</div>
              <div className="text-xl font-semibold text-amber-700 mb-2">Industry Awards</div>
              <div className="text-gray-600">Recognition and accolades</div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-20 h-20 mx-auto mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_77845016-0cff-4a07-b056-0e435a63b242/artifacts/182ibqaq_Success%20Rate.gif" 
                  alt="Success Rate"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-5xl font-bold text-gray-900 mb-2">95%</div>
              <div className="text-xl font-semibold text-amber-700 mb-2">Success Rate</div>
              <div className="text-gray-600">Client satisfaction score</div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-20 h-20 mx-auto mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_77845016-0cff-4a07-b056-0e435a63b242/artifacts/acnb6r7x_Countries.gif" 
                  alt="Countries"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-5xl font-bold text-gray-900 mb-2">30+</div>
              <div className="text-xl font-semibold text-amber-700 mb-2">Countries</div>
              <div className="text-gray-600">Global distribution reach</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white px-4 relative" style={{ zIndex: 1 }}>
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-700 rounded-full mb-6">
              <span className="text-amber-700 text-sm font-semibold tracking-wider">TESTIMONIALS</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our <span className="text-amber-700">Published Authors</span> Say
            </h2>
            <p className="text-xl text-gray-600">
              Real success stories from authors who trusted us with their books
            </p>
          </div>

          <div className="relative">
            <div className="grid md:grid-cols-2 gap-8">
              {currentTestimonials.map((testimonial, index) => (
                <Card key={index} className="p-8 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-4">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-amber-600">{testimonial.role}</div>
                      <div className="text-xs text-gray-500">Author of "{testimonial.bookTitle}"</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="flex justify-center items-center gap-4 mt-12">
              <button
                onClick={prevPage}
                className="bg-white border-2 border-amber-700 rounded-full p-3 hover:bg-amber-50 transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-amber-700" />
              </button>
              <div className="flex gap-2">
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentPage ? 'bg-amber-600 w-8' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={nextPage}
                className="bg-white border-2 border-amber-700 rounded-full p-3 hover:bg-amber-50 transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-amber-700" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Form */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-white px-4 relative overflow-hidden" style={{ zIndex: 1 }}>
        <div className="absolute top-10 left-10 w-64 h-64 border-2 border-dashed border-amber-200 rounded-full opacity-50"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 border-2 border-dashed border-amber-200 rounded-full opacity-50"></div>
        
        <div className="container mx-auto max-w-3xl relative z-10">
          <div className="text-center mb-5 lg:mb-6">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Ready to Bring Your <span className="text-amber-600">Story</span> to Life?
            </h2>
            <p className="text-xl text-gray-600">
              Get in touch with us today and let's start your publishing journey.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <form onSubmit={handleCtaFormSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={ctaFormData.name}
                    onChange={handleCtaFormChange}
                    placeholder="Your Name"
                    required
                    className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-2 text-gray-900 placeholder:text-gray-500 transition-colors"
                  />
                </div>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={ctaFormData.email}
                    onChange={handleCtaFormChange}
                    placeholder="Email Address"
                    required
                    className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-2 text-gray-900 placeholder:text-gray-500 transition-colors"
                  />
                </div>

                {/* Country Field - Auto-detected */}
                <div className="relative">
                  <input
                    type="text"
                    name="country"
                    placeholder={geoLoading ? "Detecting country..." : "Country"}
                    value={country}
                    disabled
                    className="w-full bg-gray-50 border-0 border-b-2 border-gray-300 outline-none px-0 py-2 text-gray-700 placeholder:text-gray-500 cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="relative flex gap-2 items-end">
                <select className="bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none py-2 text-gray-900 transition-colors">
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+61">🇦🇺 +61</option>
                  <option value="+81">🇯🇵 +81</option>
                  <option value="+86">🇨🇳 +86</option>
                </select>
                <input
                  type="tel"
                  name="phone"
                  value={ctaFormData.phone}
                  onChange={handleCtaFormChange}
                  placeholder="Phone Number"
                  required
                  className="flex-1 bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-2 text-gray-900 placeholder:text-gray-500 transition-colors"
                />
              </div>

              <div className="relative">
                <textarea
                  name="projectDetails"
                  value={ctaFormData.projectDetails}
                  onChange={handleCtaFormChange}
                  placeholder="Project Details"
                  rows="4"
                  className="w-full bg-transparent border-0 border-b-2 border-gray-300 focus:border-amber-700 outline-none px-0 py-2 text-gray-900 placeholder:text-gray-500 transition-colors resize-none"
                />
              </div>

              <BlobButton
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-700 hover:bg-amber-800 text-white font-bold py-4 px-8 rounded-full transition-all whitespace-nowrap"
              >
                <Send className="w-5 h-5 inline-block mr-2" />
                Start Your Audiobook Journey
              </BlobButton>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 relative" style={{ zIndex: 1 }}>
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <img 
                src="https://customer-assets.emergentagent.com/job_book-service-lp/artifacts/f7adg5p2_logo-dark.png" 
                alt="Rushmore Publishing" 
                className="h-10 w-auto mb-4"
              />
              <p className="text-gray-400 text-sm">Making publishing simple for authors worldwide</p>
            </div>
            
            <div className="flex gap-6 text-sm">
              <a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="/terms-and-conditions" className="text-gray-400 hover:text-white transition-colors">
                Terms & Conditions
              </a>
              <a href="/disclaimer-policy" className="text-gray-400 hover:text-white transition-colors">
                Disclaimer
              </a>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-6 pt-6 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Rushmore Publishing. All rights reserved.</p>
          </div>
        </div>
      </footer>
      
      {/* Contact Form Modal */}
      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default LPAudiobook;
